import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './index.css';
import LandingPageList from './components/LandingPageList';
import VariantManager from './components/VariantManager';
import Analytics from './components/Analytics';

interface LandingPage {
    id: string;
    urlPath: string;
    cookieName: string;
    isPublic: boolean;
    createdAt: string;
}

function App() {
    const [landingPages, setLandingPages] = useState<LandingPage[]>([]);
    const [selectedLandingPageId, setSelectedLandingPageId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'pages' | 'variants' | 'analytics'>('pages');
    const [loading, setLoading] = useState(false);

    // Use environment variable if set, otherwise use the neetprep.in custom domain
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const API_BASE = import.meta.env.VITE_API_URL ||
        (isDevelopment ? 'http://localhost:8787' : 'https://neetprep.in');

    // Fetch landing pages on mount
    useEffect(() => {
        fetchLandingPages();
    }, []);

    const fetchLandingPages = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE}/admin/landing-pages`);
            const data = Array.isArray(response.data) ? response.data : [];
            setLandingPages(data);
            if (data.length > 0 && !selectedLandingPageId) {
                setSelectedLandingPageId(data[0].id);
            }
        } catch (error: any) {
            console.error('Failed to fetch landing pages:', error);
            setLandingPages([]);
            // Show user-friendly error message
            const errorMsg = error.response?.data?.error || error.message || 'Failed to load landing pages';
            console.warn('API Error:', errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectLandingPage = (id: string) => {
        setSelectedLandingPageId(id);
        setActiveTab('variants');
    };

    return (
        <div className="app">
            <header className="header">
                <h1>🚀 A/B Testing Dashboard</h1>
                <p>Manage your landing page variants and track performance</p>
            </header>

            <div className="container">
                {/* Sidebar Navigation */}
                <aside className="sidebar">
                    <nav className="nav-tabs">
                        <button
                            className={`nav-tab ${activeTab === 'pages' ? 'active' : ''}`}
                            onClick={() => setActiveTab('pages')}
                        >
                            📄 Landing Pages
                        </button>
                        <button
                            className={`nav-tab ${activeTab === 'variants' ? 'active' : ''}`}
                            onClick={() => setActiveTab('variants')}
                            disabled={!selectedLandingPageId}
                        >
                            🎯 Variants
                        </button>
                        <button
                            className={`nav-tab ${activeTab === 'analytics' ? 'active' : ''}`}
                            onClick={() => setActiveTab('analytics')}
                            disabled={!selectedLandingPageId}
                        >
                            📊 Analytics
                        </button>
                    </nav>

                    {selectedLandingPageId && (
                        <div className="selected-page-info">
                            <h3>Selected Page</h3>
                            <p>
                                {landingPages.find((p) => p.id === selectedLandingPageId)?.urlPath}
                            </p>
                        </div>
                    )}
                </aside>

                {/* Main Content */}
                <main className="main-content">
                    {loading && <div className="loading">Loading...</div>}

                    {activeTab === 'pages' && (
                        <LandingPageList
                            landingPages={landingPages}
                            selectedId={selectedLandingPageId}
                            onSelect={handleSelectLandingPage}
                            onRefresh={fetchLandingPages}
                            apiBase={API_BASE}
                        />
                    )}

                    {activeTab === 'variants' && selectedLandingPageId && (
                        <VariantManager
                            landingPageId={selectedLandingPageId}
                            apiBase={API_BASE}
                            onUpdate={() => {
                                // Refresh variants
                            }}
                        />
                    )}

                    {activeTab === 'analytics' && selectedLandingPageId && (
                        <Analytics
                            landingPageId={selectedLandingPageId}
                            apiBase={API_BASE}
                        />
                    )}
                </main>
            </div>
        </div>
    );
}

export default App;
